import { useSyncExternalStore } from "react";

import {
  getInstallPrompt,
  promptInstall,
  subscribeInstallPrompt
} from "@app/bootstrap/pwaInstall";

const isStandalone = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(display-mode: standalone)").matches;

/**
 * Exposes an in-app install affordance for the desktop PWA.
 *
 * The `beforeinstallprompt` event is captured at app startup (see
 * @app/bootstrap/pwaInstall) so it can't be missed by a late-mounting effect. This hook
 * subscribes to that store and reports whether install is currently available.
 *
 * `canInstall` is true only when the browser has offered installation
 * (Chrome/Edge fire the event) and the app isn't already installed.
 * Firefox/Safari never fire it, so `canInstall` stays false there — the caller
 * shows nothing, which is correct since they can't be prompted.
 *
 * Note: even in Chrome the event fires only when the origin passes all install
 * criteria AND Chrome decides to promote it; a recent install/uninstall on the
 * same origin suppresses it for a cooldown. Absence of the button is therefore
 * expected in some states and not a bug.
 */
export const useInstallPrompt = () => {
  const hasPrompt = useSyncExternalStore(
    subscribeInstallPrompt,
    () => getInstallPrompt() !== null,
    () => false // server snapshot: never installable during SSR/prerender
  );

  return {
    canInstall: hasPrompt && !isStandalone(),
    promptInstall
  };
};
